/**
 * Firmware upgrade state and actions.
 */
import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import type { FirmwareTab, OtaState } from '@/core/firmware-types'
import { FIRMWARE_TABS } from '@/core/firmware-types'
import {
  buildFirmwareUrl,
  compareFirmwareVersion,
  getDeviceCurrentVersion,
  getFirmwareHistoryList,
  getFirmwareMockData,
  getTabsWithFirmwareUpdate,
  hasAnyFirmwareUpdate,
  hasFirmwareUpdate,
} from '@/core/firmware-mock-data'
import {
  cancelFirmwareUpgrade,
  pickHidDeviceManually,
  runFirmwareUpgrade,
  validateConnectionForTab,
} from '@/core/firmware-upgrade-service'
import { formatBytes } from '@/api/device/firmware'
import { useDeviceStore } from './device'

export type { FirmwareTab }

export const useFirmwareStore = defineStore('firmware', () => {
  const deviceStore = useDeviceStore()

  const activeTab = ref<FirmwareTab>('earphone')
  const otaState = ref<OtaState>('idle')
  const isChecking = ref(false)
  const isUpgrading = ref(false)
  const error = ref('')
  const statusMessage = ref('')
  const logs = ref<string[]>([])

  const upgradeProgress = ref({
    progress: 0,
    currentBytes: 0,
    totalBytes: 0,
    message: '',
  })

  const selectedVersion = ref('')
  const customFirmwareFile = ref<File | null>(null)

  const deviceDetailRecord = computed(() => {
    const d = deviceStore.deviceDetail
    const s = deviceStore.deviceStatus
    if (!d && !s) return null

    return {
      ...(d || {}),
      softwareVersion: d?.softwareVersion ?? s?.softwareVersion,
      bluetoothVersion: d?.bluetoothVersion ?? s?.bluetoothVersion,
      noiseCancelVersion: d?.noiseCancelVersion ?? s?.noiseCancelVersion,
      dongleVersion: d?.dongleVersion ?? s?.dongleVersion,
    } as Record<string, unknown>
  })

  const currentVersion = computed(() =>
    getDeviceCurrentVersion(activeTab.value, deviceDetailRecord.value)
  )
  const mockData = computed(() => getFirmwareMockData(activeTab.value))
  const latestVersion = computed(() => mockData.value.latestVersion)
  const hasNewVersion = computed(() =>
    hasFirmwareUpdate(activeTab.value, deviceDetailRecord.value)
  )
  const tabsWithUpdate = computed(() =>
    getTabsWithFirmwareUpdate(deviceDetailRecord.value)
  )
  const anyUpdate = computed(() => hasAnyFirmwareUpdate(deviceDetailRecord.value))
  const historyList = computed(() => getFirmwareHistoryList(activeTab.value))

  const progressText = computed(() => {
    const { progress, message } = upgradeProgress.value
    if (otaState.value === 'completed') return 'Upgrade complete'
    if (message) return message
    if (progress === 0) return 'Preparing...'
    if (progress === 100) return 'Upgrade complete'
    return `${progress}%`
  })

  const fileSizeText = computed(() => {
    const { currentBytes, totalBytes } = upgradeProgress.value
    return `${formatBytes(currentBytes)} / ${formatBytes(totalBytes)}`
  })

  function addLog(message: string) {
    logs.value.push(`[${new Date().toLocaleTimeString()}] ${message}`)
    if (logs.value.length > 100) logs.value.shift()
  }

  function setActiveTab(tab: FirmwareTab) {
    if (isUpgrading.value) return
    activeTab.value = tab
    error.value = ''
    selectedVersion.value = ''
    customFirmwareFile.value = null
  }

  async function checkUpdate(): Promise<boolean> {
    isChecking.value = true
    error.value = ''
    statusMessage.value = ''

    try {
      await new Promise(resolve => setTimeout(resolve, 300))
      if (!deviceStore.isConnected) {
        error.value = 'Device is not connected'
        return false
      }

      selectedVersion.value = latestVersion.value
      statusMessage.value = hasNewVersion.value
        ? `New version found: ${latestVersion.value}`
        : 'Already on the latest version'
      return hasNewVersion.value
    } finally {
      isChecking.value = false
    }
  }

  async function startUpgrade(version?: string, hidDevice?: HIDDevice | null): Promise<void> {
    if (isUpgrading.value) return

    const ver = (version || selectedVersion.value || latestVersion.value).trim()
    if (!ver) {
      error.value = 'Please select a firmware version'
      return
    }

    if (!navigator.hid) {
      error.value = 'Current browser does not support WebHID'
      return
    }

    const devices = await navigator.hid.getDevices()
    const conn = validateConnectionForTab(activeTab.value, devices, hidDevice)
    if (!conn.ok) {
      error.value = conn.message || 'Connection type mismatch'
      return
    }

    isUpgrading.value = true
    otaState.value = 'transferring'
    error.value = ''
    logs.value = []
    statusMessage.value = 'Preparing upgrade...'
    upgradeProgress.value = {
      progress: 0,
      currentBytes: 0,
      totalBytes: 0,
      message: 'Preparing upgrade...',
    }

    const totalEstimate = 512 * 1024

    try {
      await runFirmwareUpgrade(
        activeTab.value,
        ver,
        {
          onProgress: (percent, message) => {
            upgradeProgress.value = {
              progress: percent,
              currentBytes: Math.floor((percent / 100) * totalEstimate),
              totalBytes: totalEstimate,
              message,
            }
            statusMessage.value = message
          },
          onComplete: () => {
            otaState.value = 'completed'
            isUpgrading.value = false
            upgradeProgress.value.progress = 100
            statusMessage.value = 'Upgrade complete'
            addLog('Upgrade succeeded')
            void deviceStore.refreshStatus()
          },
          onError: err => {
            otaState.value = 'failed'
            isUpgrading.value = false
            error.value = err.message
            addLog(`Upgrade failed: ${err.message}`)
          },
          onLog: (msg, level) => addLog(`[${level || 'info'}] ${msg}`),
        },
        {
          firmwareFile: customFirmwareFile.value || undefined,
          firmwareUrl: customFirmwareFile.value ? undefined : buildFirmwareUrl(activeTab.value, ver),
          hidDevice,
        }
      )
    } catch (e) {
      otaState.value = 'failed'
      isUpgrading.value = false
      error.value = e instanceof Error ? e.message : 'Upgrade failed'
    }
  }

  async function startUpgradeFromFile(file: File): Promise<void> {
    customFirmwareFile.value = file
    selectedVersion.value = file.name.replace(/\.[^.]+$/, '')
    await startUpgrade(selectedVersion.value)
  }

  async function manualPickDevice(): Promise<HIDDevice | null> {
    try {
      const dev = await pickHidDeviceManually()
      if (!dev) {
        error.value = 'No device selected'
        return null
      }
      if (!dev.opened) await dev.open()
      addLog(`Manually selected: ${dev.productName || 'Unknown device'}`)
      return dev
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Manual device selection failed'
      return null
    }
  }

  function cancelUpgrade() {
    cancelFirmwareUpgrade()
    isUpgrading.value = false
    otaState.value = 'idle'
    upgradeProgress.value = { progress: 0, currentBytes: 0, totalBytes: 0, message: '' }
    statusMessage.value = 'Cancelled'
  }

  function clearLogs() {
    logs.value = []
  }

  function reset() {
    cancelUpgrade()
    error.value = ''
    statusMessage.value = ''
    selectedVersion.value = ''
    customFirmwareFile.value = null
    otaState.value = 'idle'
  }

  function selectHistoryVersion(version: string) {
    selectedVersion.value = version
  }

  return {
    FIRMWARE_TABS,
    activeTab,
    otaState,
    isChecking,
    isUpgrading,
    error,
    statusMessage,
    logs,
    upgradeProgress,
    selectedVersion,
    customFirmwareFile,
    currentVersion,
    latestVersion,
    mockData,
    hasNewVersion,
    tabsWithUpdate,
    anyUpdate,
    historyList,
    progressText,
    fileSizeText,
    compareFirmwareVersion,
    setActiveTab,
    checkUpdate,
    startUpgrade,
    startUpgradeFromFile,
    manualPickDevice,
    cancelUpgrade,
    clearLogs,
    reset,
    selectHistoryVersion,
    buildFirmwareUrl,
  }
})
